import React, { useState, useEffect } from 'react';
import { rollChallenge } from '../utils/rollChallenge';
import config from '../config';
import {
  PageContainer,
  ColumnContainer,
  SectionHeader,
  Container,
  StatsTable,
  StatsRow,
  StatsCell,
  StatsHeader,
  StatsHeaderCell,
  ProgressBarContainer,
  ProgressBar,
} from './index';
import { ethers } from 'ethers';
import challengeConfig from '../config/challengeConfig';

const NUM_ROLLS = 4400;

const getLatestConfig = () => {
  const latestVersion = challengeConfig.versions[challengeConfig.versions.length - 1].version;
  return challengeConfig[latestVersion];
};

const makeZeroedStats = () => {
  const config = getLatestConfig();
  const ENEMY_TYPES = config.enemy;
  const stats = {};
  ENEMY_TYPES.forEach(enemy => {
    stats[enemy] = {
      primary: {},
      secondary: {},
      grenade: {},
      stratagem: {},
    };
    config.primary.forEach(item => stats[enemy].primary[item] = 0);
    config.secondary.forEach(item => stats[enemy].secondary[item] = 0);
    config.grenade.forEach(item => stats[enemy].grenade[item] = 0);
    config.stratagem.forEach(item => stats[enemy].stratagem[item] = 0);
  });
  return stats;
};

const ENEMY_TYPES = getLatestConfig().enemy;

const LoadoutStatsView = ({ provider }) => {
  const [stats, setStats] = useState(makeZeroedStats());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const calculateStats = async () => {
      if (!provider) {
        console.error('No provider available');
        setIsLoading(false);
        return;
      }

      const counts = makeZeroedStats();
      const currentBlock = await provider.getBlockNumber();
      const startBlock = currentBlock;
      const userAddress = ethers.constants.AddressZero;

      for (let i = 0; i < NUM_ROLLS; i++) {
        const blockNumber = startBlock - i;
        try {
          const result = await rollChallenge(provider, blockNumber, userAddress);
          const { primary, secondary, grenade, stratagem1, stratagem2, stratagem3, stratagem4 } = result.loadout;
          const enemy = result.mission.enemy;
          if (!ENEMY_TYPES.includes(enemy)) continue;

          [primary, secondary, grenade].forEach((item, index) => {
            const category = ['primary', 'secondary', 'grenade'][index];
            if (counts[enemy][category][item] !== undefined) {
              counts[enemy][category][item]++;
            }
          });

          [stratagem1, stratagem2, stratagem3, stratagem4].forEach((strat) => {
            if (counts[enemy].stratagem[strat] !== undefined) {
              counts[enemy].stratagem[strat]++;
            }
          });
        } catch (error) {
          console.error('Error rolling challenge:', error);
        }
      }

      setStats(counts);
      setIsLoading(false);
    };

    calculateStats();
  }, [provider]);

  const renderStatsTable = (category, data) => {
    const sortedItems = Object.entries(data)
      .sort((a, b) => b[1] - a[1])
      .map(([item, count]) => ({
        name: item,
        count,
        percentage: ((count / NUM_ROLLS) * 100).toFixed(2)
      }));

    const maxCount = sortedItems.length > 0 ? sortedItems[0].count : 1;

    return (
      <StatsTable>
        <StatsHeader>
          <StatsHeaderCell>Item</StatsHeaderCell>
          <StatsHeaderCell>Count</StatsHeaderCell>
          <StatsHeaderCell>Percentage</StatsHeaderCell>
          <StatsHeaderCell>Distribution</StatsHeaderCell>
        </StatsHeader>
        {sortedItems.map((item, index) => (
          <StatsRow key={index}>
            <StatsCell>{item.name}</StatsCell>
            <StatsCell>{item.count}</StatsCell>
            <StatsCell>{item.percentage}%</StatsCell>
            <StatsCell>
              <ProgressBarContainer>
                <ProgressBar progress={(item.count / maxCount) * 100} />
              </ProgressBarContainer>
            </StatsCell>
          </StatsRow>
        ))}
      </StatsTable>
    );
  };

  if (config.BEACHHEAD_BUILD !== 'development') {
    return (
      <PageContainer>
        <ColumnContainer>
          <Container>
            <SectionHeader>Loadout Stats</SectionHeader>
            <div>This view is only available in development mode.</div>
          </Container>
        </ColumnContainer>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <ColumnContainer>
          <Container>
            <SectionHeader>Loading Statistics...</SectionHeader>
          </Container>
        </ColumnContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ColumnContainer>
        <Container>
          <SectionHeader>Loadout Statistics (out of {NUM_ROLLS} rolls per enemy type)</SectionHeader>
          {ENEMY_TYPES.map(enemy => (
            <div key={enemy} style={{ marginBottom: 40 }}>
              <SectionHeader>{enemy}</SectionHeader>
              <SectionHeader>Primary Weapons</SectionHeader>
              {renderStatsTable('primary', stats[enemy].primary)}
              <SectionHeader>Secondary Weapons</SectionHeader>
              {renderStatsTable('secondary', stats[enemy].secondary)}
              <SectionHeader>Grenades</SectionHeader>
              {renderStatsTable('grenade', stats[enemy].grenade)}
              <SectionHeader>Stratagems (any slot)</SectionHeader>
              {renderStatsTable('stratagem', stats[enemy].stratagem)}
            </div>
          ))}
        </Container>
      </ColumnContainer>
    </PageContainer>
  );
};

export default LoadoutStatsView;
